import { useState } from 'react'
import { Loader2, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/custom/searchable-select'
import { OgsmAccessField } from '@/components/custom/admin/ogsm-access-field'
import { UserAppAccessFields } from '@/components/custom/admin/user-app-access-fields'
import {
  UserSheet,
  UserSheetBody,
  UserSheetFieldLabel,
  UserSheetFooter,
} from '@/components/custom/admin/user-sheet'
import { useInviteInternalUser } from '@/hooks/use-admin'
import { useInvitePortalUser } from '@/hooks/use-portal'
import { useCompaniesDropdownSearch } from '@/hooks/use-reference-data'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { showSuccess, showError } from '@/lib/toast'
import {
  getAllowedInternalDomains,
  isInternalEmail,
  isValidEmail,
} from '@/lib/validation'
import { Typography } from '@/components/ui/typography'
import type { OgsmAccess } from '@/services/portal.service'
import type { AllowedApp } from '@/types/api'

type InviteMode = 'internal' | 'portco-picker' | 'portco-implicit'

interface InviteUserSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: InviteMode
  returnFocusRef?: React.RefObject<HTMLElement | null>
  initialCompany?: { id: string; name: string }
  /** Shows the OGSM access field and sends ogsmAccess. */
  askOgsmAccess?: boolean
}

const INTERNAL_ROLES = [
  { value: 'GREYBULL_MEMBER', label: 'Member' },
  { value: 'HEAD_OF_PORTFOLIO', label: 'Head of Portfolio' },
  { value: 'GREYBULL_ADMIN', label: 'Admin' },
]

const PORTCO_ROLES = [
  { value: 'PORTCO_STANDARD', label: 'Member' },
  { value: 'PORTCO_ADMIN', label: 'Admin' },
]

export function InviteUserSheet({
  open,
  onOpenChange,
  mode,
  returnFocusRef,
  initialCompany,
  askOgsmAccess = false,
}: InviteUserSheetProps) {
  const isInternal = mode === 'internal'
  const showOgsmAccess = askOgsmAccess
  const defaultRole = isInternal ? 'GREYBULL_MEMBER' : 'PORTCO_STANDARD'
  const defaultOgsmAccess: OgsmAccess = isInternal ? 'EDIT' : 'READ_ONLY'

  const [email, setEmail] = useState('')
  const [role, setRole] = useState(defaultRole)
  const [allowedApps, setAllowedApps] = useState<AllowedApp[]>(['compass'])
  const [ogsmAccess, setOgsmAccess] = useState<OgsmAccess>(defaultOgsmAccess)
  const [companyId, setCompanyId] = useState(initialCompany?.id ?? '')
  const [companySearch, setCompanySearch] = useState('')
  const debouncedSearch = useDebouncedValue(companySearch, 300)

  const inviteInternal = useInviteInternalUser()
  const invitePortco = useInvitePortalUser()

  const { data: companiesData, isLoading: companiesLoading } =
    useCompaniesDropdownSearch({
      search: debouncedSearch,
      enabled: open && mode === 'portco-picker' && !initialCompany,
    })

  const resetForm = () => {
    setEmail('')
    setRole(defaultRole)
    setAllowedApps(['compass'])
    setOgsmAccess(defaultOgsmAccess)
    setCompanyId(initialCompany?.id ?? '')
    setCompanySearch('')
  }

  const trimmedEmail = email.trim().toLowerCase()
  const effectiveCompanyId = initialCompany?.id ?? companyId
  const emailFormatValid = isValidEmail(trimmedEmail)
  const internalDomainValid = isInternal ? isInternalEmail(trimmedEmail) : true
  const showInternalDomainHint =
    isInternal && emailFormatValid && !internalDomainValid
  const allowedInternalDomains = getAllowedInternalDomains()

  const canSubmit = (() => {
    if (allowedApps.length === 0) return false
    if (!emailFormatValid) return false
    if (isInternal && !internalDomainValid) return false
    if (mode === 'portco-picker') return Boolean(effectiveCompanyId)
    return true
  })()

  // Admins always edit OGSM (backend rule); the draft level waits underneath.
  const ogsmLocked = role === (isInternal ? 'GREYBULL_ADMIN' : 'PORTCO_ADMIN')

  const isPending = isInternal
    ? inviteInternal.isPending
    : invitePortco.isPending

  const handleSubmit = async () => {
    if (!canSubmit) return
    try {
      if (isInternal) {
        await inviteInternal.mutateAsync({
          email: trimmedEmail,
          role,
          allowedApps,
          // A locked internal invite sends what the field shows, so the invitation row agrees.
          ...(showOgsmAccess
            ? { ogsmAccess: ogsmLocked ? 'EDIT' : ogsmAccess }
            : {}),
        })
      } else {
        await invitePortco.mutateAsync({
          email: trimmedEmail,
          role,
          allowedApps,
          ...(mode === 'portco-picker'
            ? { companyId: effectiveCompanyId }
            : {}),
          ...(showOgsmAccess ? { ogsmAccess } : {}),
        })
      }
      showSuccess(`Invitation sent to ${trimmedEmail}`)
      resetForm()
      onOpenChange(false)
    } catch (error) {
      showError(error, 'Failed to send invitation')
    }
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) resetForm()
    onOpenChange(isOpen)
  }

  const title = isInternal
    ? 'Invite team member'
    : initialCompany
      ? `Invite to ${initialCompany.name}`
      : 'Invite PortCo user'
  const subtitle = isInternal
    ? "They'll receive an email to set up their Greybull account."
    : mode === 'portco-picker'
      ? 'The email domain must match an allowed domain on the selected company.'
      : "The email domain must match your company's allowed domains."

  const roleOptions = isInternal ? INTERNAL_ROLES : PORTCO_ROLES

  return (
    <UserSheet
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      subtitle={subtitle}
      returnFocusRef={returnFocusRef}
    >
      <UserSheetBody>
        {mode === 'portco-picker' && initialCompany ? (
          <div>
            <UserSheetFieldLabel>Company</UserSheetFieldLabel>
            <div className="rounded-md border border-primary/15 bg-primary/[0.02] px-3 py-2.5">
              <Typography variant="body-sm" className="text-primary">
                {initialCompany.name}
              </Typography>
            </div>
          </div>
        ) : mode === 'portco-picker' ? (
          <div>
            <UserSheetFieldLabel>Company</UserSheetFieldLabel>
            <SearchableSelect
              ariaLabel="Company"
              options={(companiesData?.data ?? []).map((c) => ({
                value: c.id,
                label: c.displayName,
              }))}
              value={companyId}
              onValueChange={(v) => setCompanyId(v ?? '')}
              onSearchChange={setCompanySearch}
              placeholder="Select a company…"
              searchPlaceholder="Search companies..."
              isLoading={companiesLoading}
              showSearchIcon
            />
          </div>
        ) : null}

        <div>
          <UserSheetFieldLabel htmlFor="invite-user-email">
            Email
          </UserSheetFieldLabel>
          <Input
            id="invite-user-email"
            type="email"
            placeholder={
              isInternal ? 'name@greybullstewardship.com' : 'user@company.com'
            }
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSubmit && !isPending) {
                void handleSubmit()
              }
            }}
            autoFocus
          />
          {showInternalDomainHint && (
            <Typography
              variant="body-sm"
              className="mt-1.5 text-[12px] text-destructive"
            >
              Internal invites must use{' '}
              {allowedInternalDomains.map((d, i) => (
                <span key={d}>
                  {i > 0 && ' or '}
                  <span className="font-medium">@{d}</span>
                </span>
              ))}
              .
            </Typography>
          )}
        </div>

        <div>
          <UserSheetFieldLabel htmlFor="invite-user-role">
            Role
          </UserSheetFieldLabel>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger
              id="invite-user-role"
              className="h-10 border-primary/20"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {roleOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {showOgsmAccess && (
          <OgsmAccessField
            id="invite-user-ogsm-access"
            value={ogsmAccess}
            onChange={setOgsmAccess}
            locked={ogsmLocked}
            disabled={isPending}
          />
        )}

        <UserAppAccessFields
          allowedApps={allowedApps}
          onAllowedAppsChange={setAllowedApps}
          context="invite"
          includeDealReview={isInternal}
        />
      </UserSheetBody>

      <UserSheetFooter>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleOpenChange(false)}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={() => void handleSubmit()}
          disabled={!canSubmit || isPending}
          className="gap-1.5"
        >
          {isPending ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Mail className="size-3.5" />
          )}
          Send invite
        </Button>
      </UserSheetFooter>
    </UserSheet>
  )
}
